import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SSEService } from '@/services/sseService';
import { extractArrayData } from '@/lib/utils';
import {
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsUnreadSummaryQueryKey,
  postApiWorkspacesWorkspaceIdConversationsConversationIdRead,
} from '@/api/generated/conversations/conversations';
import { useConversationStore } from '@/stores/useConversationStore';
import { useGetApiUsersMe } from '@/api/generated/users/users';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type {
  ConversationItem,
  ConversationDetail,
  MessageItem,
  CustomerDto,
  MessageSenderType,
  SSEConversationCreatedEvent,
  SSEMessageAddedEvent,
  SSEConversationTitleUpdatedEvent,
  SSECustomerLinkedEvent,
  SSECustomerUpdatedEvent,
  SSEConversationClosedEvent,
  SSEHumanJoinedEvent,
  SSELastMessageUpdatedEvent,
  SSEAiPreparingEvent,
  SSEMessageCompletedEvent,
  SSEConversationReadEvent,
  SSEWaitingForHumanEvent,
} from '@/types';

export interface UseWorkspaceSSEReturn {
  isConnected: boolean;
  error: unknown | null;
  reconnect: () => void;
  lastEvent: unknown | null;
}

export const useWorkspaceSSE = (): UseWorkspaceSSEReturn => {
  const queryClient = useQueryClient();
  const { data: userProfile } = useGetApiUsersMe();
  const { activeWorkspaceId, workspaces } = useWorkspaceStore();
  const { token } = useAuthStore();
  const settings = (userProfile as any)?.settings;

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [lastEvent, setLastEvent] = useState<unknown | null>(null);
  const sseRef = useRef<SSEService | null>(null);

  const reconnect = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.reconnect();
    }
  }, []);

  useEffect(() => {
    const isValidWorkspace = workspaces.some((w) => w.id === activeWorkspaceId);
    if (!activeWorkspaceId || !token || !isValidWorkspace) return;

    const sseUrl = `/api/workspaces/${activeWorkspaceId}/sse`;
    const sse = new SSEService(sseUrl);
    sseRef.current = sse;

    sse.onOpenCallback = () => {
      setIsConnected(true);
      setError(null);
    };

    sse.onErrorCallback = (err) => {
      setIsConnected(false);
      setError(err);
    };

    // 1. conversation_created
    const handleConversationCreated = (data: unknown) => {
      const convEvent = data as SSEConversationCreatedEvent;
      const convId = convEvent?.conversationId || convEvent?.id;
      if (!convId) return;
      setLastEvent({ type: 'conversation_created', data: convEvent });

      const now = convEvent.createdAt || convEvent.startedAt || new Date().toISOString();
      const newConv: ConversationItem = {
        id: convId,
        storeId: convEvent.storeId,
        title: convEvent.title || 'Nova Conversa',
        customerName: convEvent.customerName || null,
        customerEmail: (convEvent as any).customerEmail || (convEvent as any).email || null,
        status: convEvent.status || 'Active',
        createdAtUtc: now,
        updatedAtUtc: now,
        startedAt: now,
        unreadCount: convEvent.unreadCount || 1,
        lastMessage: null,
        lastMessageAt: now,
      };

      queryClient.setQueriesData<any>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (old: any) => {
          if (!old || !Array.isArray(old.pages) || old.pages.length === 0) return old;
          
          const firstPage = old.pages[0];
          if (!Array.isArray(firstPage)) return old;
          
          const exists = old.pages.some((page: any) => 
            Array.isArray(page) && page.some((c: ConversationItem) => c.id === convId)
          );
          if (exists) return old;

          return {
            ...old,
            pages: [[newConv, ...firstPage], ...old.pages.slice(1)]
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 2. message_added / NewMessage / message_created
    const handleMessageAdded = (data: unknown) => {
      const msgEvent = data as SSEMessageAddedEvent;
      const convId = msgEvent?.conversationId;
      if (!convId) return;
      setLastEvent({ type: 'message_added', data: msgEvent });

      // Play Sound
      if (settings?.soundEnabled) {
        new Audio('/sounds/notification.mp3').play().catch(() => {});
      }

      const activeConvId = useConversationStore.getState().activeConversationId;
      const isCurrentlyOpen = activeConvId === convId;

      if (isCurrentlyOpen) {
        postApiWorkspacesWorkspaceIdConversationsConversationIdRead(activeWorkspaceId!, convId).catch(() => {});
      }

      const msgId = msgEvent.id || msgEvent.messageId || `msg-${Date.now()}`;
      const createdAt = msgEvent.createdAtUtc || msgEvent.createdAt || new Date().toISOString();
      const content = msgEvent.content || '';

      const message: MessageItem = {
        id: msgId,
        conversationId: convId,
        content,
        senderType: (msgEvent.senderType as MessageSenderType) || 'Client',
        createdAt,
        createdAtUtc: createdAt,
        guestName: msgEvent.senderName,
        senderName: msgEvent.senderName,
        senderId: msgEvent.senderId,
        clientId: msgEvent.clientId,
        payload: msgEvent.payload,
        toolCallId: msgEvent.toolCallId,
        toolName: msgEvent.toolName,
      };

      // Update messages cache for this conversation with robust deduplication and optimistic reconciliation
      queryClient.setQueriesData<any>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, convId) },
        (old: any) => {
          const oldMessages = extractArrayData<MessageItem>(old);

          // 1. Check if message already exists by canonical ID
          const existingByIdIndex = oldMessages.findIndex((m) => m.id === message.id);
          if (existingByIdIndex !== -1) {
            const updated = [...oldMessages];
            updated[existingByIdIndex] = { ...updated[existingByIdIndex], ...message, isPending: false };
            if (Array.isArray(old)) return updated;
            if (old && typeof old === 'object') {
              if (Array.isArray(old.items)) return { ...old, items: updated };
              if (Array.isArray(old.data)) return { ...old, data: updated };
              if (Array.isArray(old.messages)) return { ...old, messages: updated };
            }
            return updated;
          }

          // 2. Check if there's a matching pending optimistic message (by clientId or matching content/sender)
          const pendingIndex = oldMessages.findIndex(
            (m) => m.isPending && (
              (message.clientId && m.clientId === message.clientId) ||
              (m.content === message.content && m.senderType === message.senderType)
            )
          );

          let newArray: MessageItem[];
          if (pendingIndex !== -1) {
            newArray = [...oldMessages];
            newArray[pendingIndex] = { ...message, isPending: false };
          } else {
            newArray = [...oldMessages, message];
          }

          if (Array.isArray(old)) return newArray;
          if (old && typeof old === 'object') {
            if (Array.isArray(old.items)) return { ...old, items: newArray };
            if (Array.isArray(old.data)) return { ...old, data: newArray };
            if (Array.isArray(old.messages)) return { ...old, messages: newArray };
          }
          return newArray;
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, convId),
        exact: false,
      });
      
      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsUnreadSummaryQueryKey(activeWorkspaceId!),
        exact: false,
      });

      // Update conversation list item lastMessage & unreadCount
      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === convId) {
              const newUnreadCount = isCurrentlyOpen
                ? 0
                : (typeof msgEvent.unreadCount === 'number' ? msgEvent.unreadCount : (conv.unreadCount || 0) + 1);

              return {
                ...conv,
                lastMessage: {
                  id: msgId,
                  content,
                  senderType: msgEvent.senderType || 'Customer',
                  senderName: msgEvent.senderName || null,
                  senderId: msgEvent.senderId || null,
                  createdAtUtc: createdAt,
                },
                lastMessageAt: createdAt,
                updatedAtUtc: createdAt,
                unreadCount: newUnreadCount,
              };
            }
            return conv;
          });
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 3. conversation_title_updated
    const handleTitleUpdated = (data: unknown) => {
      const titleEvent = data as SSEConversationTitleUpdatedEvent;
      if (!titleEvent?.conversationId) return;
      setLastEvent({ type: 'conversation_title_updated', data: titleEvent });

      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            title: titleEvent.title ?? oldDetail.title,
          };
        }
      );

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === titleEvent.conversationId) {
              return {
                ...conv,
                title: titleEvent.title ?? conv.title,
              };
            }
            return conv;
          });
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 4. customer_linked
    const handleCustomerLinked = (data: unknown) => {
      const linkEvent = data as SSECustomerLinkedEvent;
      if (!linkEvent?.conversationId) return;
      setLastEvent({ type: 'customer_linked', data: linkEvent });

      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            customerId: linkEvent.customerId ?? oldDetail.customerId,
            customerName: linkEvent.name ?? oldDetail.customerName,
            customerEmail: linkEvent.email ?? oldDetail.customerEmail,
            customerPhone: linkEvent.phone ?? oldDetail.customerPhone,
          };
        }
      );

      queryClient.setQueriesData<CustomerDto>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, ) },
        (oldCustomer) => ({
          id: linkEvent.customerId,
          name: linkEvent.name ?? oldCustomer?.name,
          email: linkEvent.email ?? oldCustomer?.email,
          phone: linkEvent.phone ?? oldCustomer?.phone,
          storeId: linkEvent.storeId ?? oldCustomer?.storeId,
        })
      );

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === linkEvent.conversationId) {
              return {
                ...conv,
                customerId: linkEvent.customerId ?? conv.customerId,
                customerName: linkEvent.name ?? conv.customerName,
                customerEmail: linkEvent.email ?? conv.customerEmail,
                customerPhone: linkEvent.phone ?? conv.customerPhone,
              };
            }
            return conv;
          });
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 5. customer_updated
    const handleCustomerUpdated = (data: unknown) => {
      const custEvent = data as SSECustomerUpdatedEvent;
      if (!custEvent) return;
      setLastEvent({ type: 'customer_updated', data: custEvent });

      const name = custEvent.name ?? custEvent.customerName;
      const email = custEvent.email ?? custEvent.customerEmail;
      const phone = custEvent.phone ?? custEvent.customerPhone;

      if (custEvent.conversationId) {
        queryClient.setQueriesData<ConversationDetail>(
          { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
          (oldDetail) => {
            if (!oldDetail) return oldDetail;
            return {
              ...oldDetail,
              customerName: name !== undefined ? name : oldDetail.customerName,
              customerEmail: email !== undefined ? email : oldDetail.customerEmail,
              customerPhone: phone !== undefined ? phone : oldDetail.customerPhone,
            };
          }
        );

        queryClient.setQueriesData<CustomerDto>(
          { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, ) },
          (oldCustomer) => {
            if (!oldCustomer) return oldCustomer;
            return {
              ...oldCustomer,
              name: name !== undefined ? name : oldCustomer.name,
              email: email !== undefined ? email : oldCustomer.email,
              phone: phone !== undefined ? phone : oldCustomer.phone,
            };
          }
        );
      }

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            const matches =
              (custEvent.conversationId && conv.id === custEvent.conversationId) ||
              (custEvent.customerId && conv.customerId === custEvent.customerId);
            if (matches) {
              return {
                ...conv,
                customerName: name !== undefined ? name : conv.customerName,
                customerEmail: email !== undefined ? email : conv.customerEmail,
                customerPhone: phone !== undefined ? phone : conv.customerPhone,
              };
            }
            return conv;
          });
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 6. conversation_closed
    const handleConversationClosed = (data: unknown) => {
      const closedEvent = data as SSEConversationClosedEvent;
      if (!closedEvent?.conversationId) return;
      setLastEvent({ type: 'conversation_closed', data: closedEvent });

      const closedTime = new Date().toISOString();

      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            status: 'Closed',
            closedAtUtc: closedTime,
          };
        }
      );

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === closedEvent.conversationId) {
              return {
                ...conv,
                status: 'Closed',
                closedAtUtc: closedTime,
              };
            }
            return conv;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ),
      });
    };

    // 7. human_joined / status_change
    const handleHumanJoined = (data: unknown) => {
      const humanEvent = data as SSEHumanJoinedEvent & {
        status?: string;
        assignedAdminId?: string;
        handoffReason?: string;
      };
      if (!humanEvent?.conversationId) return;
      setLastEvent({ type: 'human_joined', data: humanEvent });

      const agentId = humanEvent.agentId || humanEvent.assignedAdminId || null;
      const status = (humanEvent.status as any) || 'HumanAssigned';

      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            status,
            assignedUserId: agentId ?? oldDetail.assignedUserId,
            assignedAdminId: agentId ?? oldDetail.assignedAdminId,
            handoffReason: humanEvent.handoffReason ?? oldDetail.handoffReason,
          };
        }
      );

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === humanEvent.conversationId) {
              return {
                ...conv,
                status,
                assignedUserId: agentId ?? conv.assignedUserId,
                assignedAdminId: agentId ?? conv.assignedAdminId,
                handoffReason: humanEvent.handoffReason ?? conv.handoffReason,
              };
            }
            return conv;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ),
      });
    };

    // Fallback: last_message_updated
    const handleLastMessageUpdated = (data: unknown) => {
      const lastMsgEvent = data as SSELastMessageUpdatedEvent;
      if (!lastMsgEvent?.conversationId) return;
      setLastEvent({ type: 'last_message_updated', data: lastMsgEvent });

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === lastMsgEvent.conversationId) {
              const prev =
                typeof conv.lastMessage === 'object' && conv.lastMessage !== null
                  ? conv.lastMessage
                  : null;
              return {
                ...conv,
                lastMessage: prev
                  ? {
                      ...prev,
                      content: lastMsgEvent.lastMessage ?? prev.content,
                      createdAtUtc: lastMsgEvent.lastMessageAt ?? prev.createdAtUtc,
                    }
                  : {
                      id: `last-${Date.now()}`,
                      content: lastMsgEvent.lastMessage,
                      senderType: 'Customer',
                      senderName: null,
                      senderId: null,
                      createdAtUtc: lastMsgEvent.lastMessageAt || new Date().toISOString(),
                    },
                lastMessageAt: lastMsgEvent.lastMessageAt ?? conv.lastMessageAt,
                updatedAtUtc: lastMsgEvent.lastMessageAt ?? conv.updatedAtUtc,
              };
            }
            return conv;
          });
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    
    // 8. ai_preparing
    const handleAiPreparing = (data: unknown) => {
      const aiEvent = data as SSEAiPreparingEvent;
      if (!aiEvent?.conversationId) return;
      setLastEvent({ type: 'ai_preparing', data: aiEvent });
    };

    // 9. message_completed
    const handleMessageCompleted = (data: unknown) => {
      const msgEvent = data as SSEMessageCompletedEvent;
      if (!msgEvent?.conversationId) return;
      setLastEvent({ type: 'message_completed', data: msgEvent });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, ),
        exact: false,
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 10. conversation_read
    const handleConversationRead = (data: unknown) => {
      const readEvent = data as SSEConversationReadEvent;
      const convId = readEvent?.conversationId;
      if (!convId) return;
      setLastEvent({ type: 'conversation_read', data: readEvent });

      const targetUnread = typeof readEvent.unreadCount === 'number' ? readEvent.unreadCount : 0;

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === convId) {
              return {
                ...conv,
                unreadCount: targetUnread,
              };
            }
            return conv;
          });
      });


      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            unreadCount: targetUnread,
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsUnreadSummaryQueryKey(activeWorkspaceId!),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
        exact: false,
      });
    };

    // 11. waiting_for_human
    const handleWaitingForHuman = (data: unknown) => {
      const waitEvent = data as SSEWaitingForHumanEvent;
      const convId = waitEvent?.conversationId;
      if (!convId) return;
      setLastEvent({ type: 'waiting_for_human', data: waitEvent });

      // Desktop Push Notification and Sound
      if (settings?.inAppNotificationsEnabled || settings?.desktopNotificationsEnabled) {
        if (settings?.soundEnabled) {
          new Audio('/sounds/notification.mp3').play().catch(() => {});
        }
        if (settings?.desktopNotificationsEnabled && document.visibilityState === 'hidden') {
          if (Notification.permission === 'granted') {
            new Notification('Suporte Solicitado', { body: 'Um visitante precisa de ajuda humana!' });
          }
        }
      }

      queryClient.setQueriesData<ConversationDetail>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ) },
        (oldDetail) => {
          if (!oldDetail) return oldDetail;
          return {
            ...oldDetail,
            status: 'WaitingForHuman',
          };
        }
      );

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === convId) {
              return {
                ...conv,
                status: 'WaitingForHuman',
              };
            }
            return conv;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, ),
      });

      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
      });
    };

    // Register all user-level event listeners (supporting both snake_case and PascalCase / aliases)

    const unsubs = [
      // 1. conversation_created
      sse.addEventListener('conversation_created', handleConversationCreated),
      sse.addEventListener('ConversationCreated', handleConversationCreated),

      // 2. message_added
      sse.addEventListener('message_added', handleMessageAdded),
      sse.addEventListener('MessageAdded', handleMessageAdded),
      sse.addEventListener('message', handleMessageAdded),
      sse.addEventListener('NewMessage', handleMessageAdded),
      sse.addEventListener('message_created', handleMessageAdded),

      // 3. conversation_title_updated
      sse.addEventListener('conversation_title_updated', handleTitleUpdated),
      sse.addEventListener('ConversationTitleUpdated', handleTitleUpdated),
      sse.addEventListener('title_updated', handleTitleUpdated),

      // 4. customer_linked
      sse.addEventListener('customer_linked', handleCustomerLinked),
      sse.addEventListener('CustomerLinked', handleCustomerLinked),

      // 5. customer_updated
      sse.addEventListener('customer_updated', handleCustomerUpdated),
      sse.addEventListener('CustomerUpdated', handleCustomerUpdated),
      sse.addEventListener('ConversationCustomerInfoUpdated', handleCustomerUpdated),

      // 6. conversation_closed
      sse.addEventListener('conversation_closed', handleConversationClosed),
      sse.addEventListener('ConversationClosed', handleConversationClosed),

      // 7. human_joined / status_change
      sse.addEventListener('human_joined', handleHumanJoined),
      sse.addEventListener('HumanJoined', handleHumanJoined),
      sse.addEventListener('status_change', handleHumanJoined),
      sse.addEventListener('ConversationStatusChanged', handleHumanJoined),

      // Fallback: last_message_updated
      sse.addEventListener('last_message_updated', handleLastMessageUpdated),
      sse.addEventListener('ConversationLastMessageUpdated', handleLastMessageUpdated),

      // 8. ai_preparing
      sse.addEventListener('ai_preparing', handleAiPreparing),
      sse.addEventListener('AiPreparing', handleAiPreparing),

      // 9. message_completed
      sse.addEventListener('message_completed', handleMessageCompleted),
      sse.addEventListener('MessageCompleted', handleMessageCompleted),

      // 10. conversation_read
      sse.addEventListener('conversation_read', handleConversationRead),
      sse.addEventListener('ConversationRead', handleConversationRead),
      sse.addEventListener('conversationRead', handleConversationRead),

      // 11. waiting_for_human
      sse.addEventListener('waiting_for_human', handleWaitingForHuman),
      sse.addEventListener('WaitingForHuman', handleWaitingForHuman),
      sse.addEventListener('waitingForHuman', handleWaitingForHuman),
    ];

    sse.connect();

    return () => {
      unsubs.forEach((unsub) => unsub());
      sse.close();
      sseRef.current = null;
    };
  }, [queryClient, settings, activeWorkspaceId, token, workspaces]);

  return {
    isConnected,
    error,
    reconnect,
    lastEvent,
  };
};

export default useWorkspaceSSE;

